/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useCallback } from 'react';
import { AI_WIDGET_CONFIG } from '../configs/ai-widget-settings';

export type AgentRiskLevel = 'Conservative' | 'Balanced' | 'Aggressive';

export interface TradingAgentSettings {
    riskLevel: AgentRiskLevel;
    tradeSize: number; // in quote currency (e.g., SOL)
    maxSlippage: number; // in percent
    dailyDrawdown: number; // in percent
    profitTarget: number; // in percent
    // New Widget Settings
    widgetEnabled: boolean;
    widgetRefreshInterval: number; // ms
    widgetMaxItems: number;
    widgetFilterFavorites: boolean;
}

const AGENT_STORAGE_KEY = 'hot_trading_agent_settings_v2';

const DEFAULT_SETTINGS: TradingAgentSettings = {
    riskLevel: 'Balanced',
    tradeSize: 0.1,
    maxSlippage: 0.5,
    dailyDrawdown: 10,
    profitTarget: 25,
    widgetEnabled: AI_WIDGET_CONFIG.enabled, // Should differ to false from config
    widgetRefreshInterval: AI_WIDGET_CONFIG.refreshInterval,
    widgetMaxItems: 10,
    widgetFilterFavorites: false
};

export const useTradingAgent = () => {
    const [settings, setSettings] = useState<TradingAgentSettings>(() => {
        try {
            const stored = localStorage.getItem(AGENT_STORAGE_KEY);
            return stored ? { ...DEFAULT_SETTINGS, ...JSON.parse(stored) } : DEFAULT_SETTINGS;
        } catch {
            return DEFAULT_SETTINGS;
        }
    });

    const saveSettings = useCallback((newSettings: TradingAgentSettings) => {
        setSettings(newSettings);
        try {
            // Save to local storage for persistence across reloads
            localStorage.setItem(AGENT_STORAGE_KEY, JSON.stringify(newSettings));
            
            // Dispatch event to update widget immediately without refresh
            window.dispatchEvent(new Event('hot_agent_settings_updated'));
            
            // In a real implementation, you would also sync this to the backend User DB
            // await api.post('/ai/agent/settings', newSettings);
        } catch (e) {
            console.error("Failed to save agent settings", e);
        }
    }, []);

    return {
        settings,
        saveSettings,
    };
};